import React from 'react';
import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  Table,
  Tr,
  Td,
  Thead,
  Th,
  Tbody
} from '@chakra-ui/react';
import {
  IAuthorWithContribution,
  IAuthorWithEmail,
  IRoleType
} from '@publishing-engine/domain/models';
import { ButtonIcon } from '@morressier/alembic';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import RinggoldBadge from 'src/components/RinggoldBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/pro-solid-svg-icons';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import AuthorDetailsModal from '../../AuthorsTab/AuthorDetailsModal';
import UserName from 'src/components/User/UserName';
import {
  useAuthorData,
  useAuthorRoleCheck
} from 'src/pages/Organization/Workspace/SubmissionContexts/Users/userHook';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import ProxyUser from 'src/components/ProxyUser/ProxyUser';
import ComposeEmailModal, {
  AdHocModalOpen
} from '../../AuthorsTab/ComposeEmailModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { OrcidIDIcon } from 'src/components/User/RenderSectionDetails';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import SubmittedAuthorDetailsModal from '../../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { RenderUserBadgeList } from '../../../../pages/Organization/Workspace/SubmissionContexts/Users/UserBadgeList';

type IProps = {
  value: IAuthorWithContribution[];
  disableInteractivity?: boolean;
  config: FormFieldAuthors;
  roleContext?: IRoleType;
};
type AuthorWithStatus = (IAuthorWithEmail | ISubmissionAuthor) & {
  status?: Boolean;
};

export const AuthorsDisplay: React.FC<IProps> = ({
  value,
  disableInteractivity,
  config,
  roleContext
}) => {
  const [selectedAuthor, setSelectedAuthor] =
    React.useState<AuthorWithStatus | null>(null);
  const { orcidField, affiliationField, contributionsField } =
    config.params.validations;

  const [openSendEmailModal, setOpenSendEmailModal] =
    React.useState<AdHocModalOpen>({
      status: false,
      defaultEmail: []
    });
  const { isTaskCompleter } = useSubmissionData();
  const renderAuthorTableRow = (
    author: IAuthorWithContribution,
    index: number
  ) => {
    const { data: authorData } = useAuthorData(author);
    const isUserEditor = author?.id && useAuthorRoleCheck(author?.id);

    return (
      <Tr
        key={author.id || index}
        data-testId="author-table-row"
        gap={4}
        background="white"
        _last={{ borderBottom: 'none' }}
      >
        <Td ps={1}>{index + 1}</Td>
        <Td
          ps={1}
          cursor="pointer"
          _hover={{ bg: 'gray.50' }}
          onClick={() =>
            setSelectedAuthor({ ...author, status: !!authorData?.user })
          }
        >
          <Flex direction={'column'}>
            {author?.firstName || author?.lastName ? (
              <UserName
                firstName={author.firstName}
                middleName={author.middleName}
                lastName={author.lastName}
                lastSentStatus={authorData?.user?.lastSentEmailStatus}
                enableIcon={Object.hasOwn(
                  authorData?.user ?? {},
                  'isEmailDelivered'
                )}
                isEmailDelivered={authorData?.user?.isEmailDelivered}
                timeStamp={authorData?.user?.lastSentEmailTimeStamp}
              />
            ) : (
              <Text
                color="actionSecondaryCautionContent"
                textStyle="actionLabel"
              >
                No Name Data
              </Text>
            )}

            {author?.email ? (
              <Text
                textStyle="textContext"
                wordBreak="break-word"
                whiteSpace="normal"
              >
                {author?.email}
              </Text>
            ) : (
              <Text
                color="actionSecondaryCautionContent"
                textStyle="textContext"
              >
                No Email Data
              </Text>
            )}

            {orcidField !== 'hidden' && (
              <>
                {author.orcid ? (
                  <Flex align="flex-start" mt={1} gap={1}>
                    <Box flexShrink={0}>
                      <OrcidIDIcon value={author.orcid} />
                    </Box>
                    <Text textStyle="textContext">{author.orcid}</Text>
                  </Flex>
                ) : (
                  <Text
                    color={
                      orcidField === 'mandatory'
                        ? 'actionSecondaryCautionContent'
                        : ''
                    }
                    textStyle="textContext"
                  >
                    No ORCID provided
                  </Text>
                )}
              </>
            )}
          </Flex>

          {authorData && (
            <Flex wrap={'wrap'} gap={3} marginTop={2}>
              {authorData?.tags?.length >= 1 && (
                <RenderUserBadgeList
                  data={authorData?.tags ?? []}
                  hasIcon={true}
                  defaultLimit={2}
                />
              )}
            </Flex>
          )}
        </Td>

        {affiliationField !== 'hidden' && (
          <Td ps={0}>
            <Flex align="flex-start" gap={2}>
              {author?.ringgoldId && <RinggoldBadge />}
              {author?.affiliation ? (
                <Text textStyle="textContext" data-cy="author-affiliation">
                  {author.affiliation}
                  {author.city && `, ${author.city}`}
                  {author.country && `, ${author.country}`}
                  {author.postalCode && `, ${author.postalCode}`}
                </Text>
              ) : (
                <Text
                  color={
                    affiliationField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : ''
                  }
                  textStyle="textContext"
                >
                  No Affiliation Data
                </Text>
              )}
            </Flex>
          </Td>
        )}

        {contributionsField !== 'hidden' && (
          <Td ps={0}>
            <ContributionsBadge contributions={author.contributions} />
          </Td>
        )}

        {!disableInteractivity && (
          <Td>
            <Popover placement="bottom-end">
              <button></button>
              <PopoverTrigger>
                {/* 
                @NOTE: We should be using Button here but there some issue with Popover modal if we use it with Button component
                Issue: Popover modal is floating on Top Left corner.
              */}
                <Box
                  color="actionSecondaryContent"
                  padding={1}
                  cursor="pointer"
                >
                  <FontAwesomeIcon icon={faBars} fontSize={16} />
                </Box>
              </PopoverTrigger>

              <PopoverContent paddingY={2} maxWidth="200px">
                <PopoverBody padding={2}>
                  <List
                    padding={'0 !important'}
                    display="flex"
                    flexDirection="column"
                    gap={4}
                  >
                    <ListItem
                      cursor="pointer"
                      onClick={() =>
                        setSelectedAuthor({
                          ...author,
                          status: !!authorData?.user
                        })
                      }
                    >
                      View Author Details
                    </ListItem>
                    {isTaskCompleter && (
                      <ListItem
                        cursor="pointer"
                        onClick={() =>
                          setOpenSendEmailModal({
                            status: true,
                            defaultEmail: [author.email]
                          })
                        }
                      >
                        Send Email
                      </ListItem>
                    )}
                    {isUserEditor && (
                      <ListItem cursor="pointer">
                        <ProxyUser
                          user={{
                            firstName: author.firstName,
                            lastName: author.lastName,
                            userId: author.id || authorData?.userId || ''
                          }}
                          authorData={authorData}
                        />
                      </ListItem>
                    )}
                  </List>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Td>
        )}
      </Tr>
    );
  };

  return (
    <>
      {openSendEmailModal.status && (
        <ComposeEmailModal
          setOpenSendEmailModal={setOpenSendEmailModal}
          email={openSendEmailModal.defaultEmail}
        />
      )}
      {selectedAuthor &&
        (isTaskCompleter && selectedAuthor?.id && selectedAuthor.status ? (
          <SubmittedAuthorDetailsModal
            userId={selectedAuthor?.id || ''}
            author={selectedAuthor}
            onBackClick={() => setSelectedAuthor(null)}
            showReviewerActivity={true}
            roleContext={roleContext}
          />
        ) : (
          // Author Details Modal
          <AuthorDetailsModal
            author={selectedAuthor}
            onBackClick={() => setSelectedAuthor(null)}
          />
        ))}

      <Box overflowY={'auto'} width="100%">
        <Table size={'sm'} minWidth="100%">
          <Thead>
            <Tr
              data-testId="author-table-header"
              gap={4}
              sx={{
                '& th': {
                  textTransform: 'none',
                  color: 'black',
                  background: 'white'
                }
              }}
            >
              <Th ps={1}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  S.No.
                </Text>
              </Th>
              <Th w={'30%'} ps={1}>
                <Text textStyle="headingItem" whiteSpace="nowrap">
                  Author
                </Text>
              </Th>

              {affiliationField !== 'hidden' && (
                <Th ps={0}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Affiliation
                  </Text>
                </Th>
              )}

              {contributionsField !== 'hidden' && (
                <Th ps={0}>
                  <Flex flex={1} alignItems="center" display={'flex'}>
                    <Text textStyle="headingItem" whiteSpace={'nowrap'}>
                      CRediT Contributions
                      <Tooltip
                        hasArrow
                        shouldWrapChildren
                        placement="right"
                        label="Contributor Role Taxonomy (CRediT) Contributions"
                        borderRadius={4}
                        data-cy="contributions-info-tooltip"
                      >
                        <ButtonIcon
                          size="small"
                          ariaLabel="Back to workspace settings"
                          icon={<FontAwesomeIcon icon={faInfoCircle} />}
                          data-cy="contributions-info-icon"
                        />
                      </Tooltip>
                    </Text>
                  </Flex>
                </Th>
              )}

              {!disableInteractivity && <Th />}
            </Tr>
          </Thead>

          <Tbody>
            {value.map((author, index) => (
              <React.Fragment key={index}>
                {renderAuthorTableRow(author, index)}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
